import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import { runTileChart, tileChartKey } from './TileChartQuery';
import type { AdHocAggregateResult, AdHocTrendResult, AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import type { TileSpec } from './PinnedTileTypes';

/**
 * Loads the live data for a single pinned tile. Transient (one per rendered
 * tile); re-queries when the spec or the page's ambient filters change, and
 * dedupes identical loads so a parent re-render doesn't refetch.
 */
@injectable()
export class PinnedTileViewModel extends BaseViewModel {
    loading = false;
    error: string | null = null;
    aggregate: AdHocAggregateResult | null = null;
    trend: AdHocTrendResult | null = null;

    private key: string | null = null;

    constructor(@inject(IAnalyticsGatewayToken) private gateway: IAnalyticsGateway) {
        super();
    }

    async load(spec: TileSpec, pageFilters: AnalyticsFilter[]): Promise<void> {
        const key = tileChartKey(spec, pageFilters);
        if (this.key === key && (this.aggregate !== null || this.trend !== null || this.loading)) return;
        this.key = key;
        this.loading = true;
        this.error = null;
        this.aggregate = null;
        this.trend = null;
        this.notifyChange();
        try {
            const data = await runTileChart(this.gateway, spec, pageFilters);
            if (this.key !== key) return;
            this.aggregate = data.aggregate;
            this.trend = data.trend;
        } catch (err) {
            if (this.key !== key) return;
            this.error = err instanceof Error ? err.message : String(err);
        } finally {
            if (this.key === key) {
                this.loading = false;
                this.notifyChange();
            }
        }
    }
}
